/**
 * Offline-embedded PDF typefaces — picked from the active theme template so
 * the report matches in-app `--font-sans` (no CDN).
 *
 * clean / wellness → Nunito Sans
 * fintech / bold / minimal → Manrope
 * forest → Plus Jakarta Sans
 */
import type { jsPDF } from "jspdf";
import type { AppTemplate } from "@repo/core";
import manropeRegular from "./fonts/Manrope-Regular.base64";
import manropeBold from "./fonts/Manrope-SemiBold.base64";
import nunitoRegular from "./fonts/NunitoSans-Regular.base64";
import nunitoBold from "./fonts/NunitoSans-SemiBold.base64";
import jakartaRegular from "./fonts/PlusJakartaSans-Regular.base64";
import jakartaBold from "./fonts/PlusJakartaSans-SemiBold.base64";

export type PdfFontFamily = "Manrope" | "NunitoSans" | "PlusJakartaSans";

const FONT_FILES: Record<
  PdfFontFamily,
  { regular: string; bold: string; fileRegular: string; fileBold: string }
> = {
  Manrope: {
    regular: manropeRegular,
    bold: manropeBold,
    fileRegular: "Manrope-Regular.ttf",
    fileBold: "Manrope-SemiBold.ttf",
  },
  NunitoSans: {
    regular: nunitoRegular,
    bold: nunitoBold,
    fileRegular: "NunitoSans-Regular.ttf",
    fileBold: "NunitoSans-SemiBold.ttf",
  },
  PlusJakartaSans: {
    regular: jakartaRegular,
    bold: jakartaBold,
    fileRegular: "PlusJakartaSans-Regular.ttf",
    fileBold: "PlusJakartaSans-SemiBold.ttf",
  },
};

/** Map app template → PDF family (mirrors globals.css --font-sans). */
export function pdfFontForTemplate(template: string | null | undefined): PdfFontFamily {
  switch (template) {
    case "clean":
    case "wellness":
      return "NunitoSans";
    case "forest":
      return "PlusJakartaSans";
    case "fintech":
    case "bold":
    case "minimal":
    default:
      return "Manrope";
  }
}

/** Read live `data-template` from the document (falls back to Manrope). */
export function resolveActivePdfFont(): PdfFontFamily {
  if (typeof document === "undefined") return "Manrope";
  const template =
    document.documentElement.getAttribute("data-template") ||
    (document.documentElement.dataset.template as AppTemplate | undefined);
  return pdfFontForTemplate(template);
}

/** Active family after registerPdfFonts — used by layout helpers. */
let activePdfFont: PdfFontFamily = "Manrope";

export function getPdfFontFamily(): PdfFontFamily {
  return activePdfFont;
}

/** @deprecated Prefer getPdfFontFamily() after registerPdfFonts — value is the fallback only. */
export const PDF_FONT_FAMILY: PdfFontFamily = "Manrope";

/**
 * Body/UI sans fonts embedded in PDF (matches globals.css `--font-sans` per template).
 * Display fonts (Fredoka / Newsreader) are intentionally NOT embedded — PDF body
 * copy uses the same sans stack as most of the in-app UI chrome.
 *
 * | Theme preset | Template | PDF family        | In-app --font-sans   |
 * |--------------|----------|-------------------|----------------------|
 * | Clean        | clean    | NunitoSans        | Nunito Sans          |
 * | Fintech      | fintech  | Manrope           | Manrope              |
 * | Minimal      | minimal  | Manrope           | Manrope              |
 * | Forest       | forest   | PlusJakartaSans   | Plus Jakarta Sans    |
 */

export function registerPdfFonts(
  doc: jsPDF,
  family: PdfFontFamily = resolveActivePdfFont()
): void {
  const pack = FONT_FILES[family];
  doc.addFileToVFS(pack.fileRegular, pack.regular);
  doc.addFont(pack.fileRegular, family, "normal");
  doc.addFileToVFS(pack.fileBold, pack.bold);
  doc.addFont(pack.fileBold, family, "bold");
  doc.setFont(family, "normal");
  activePdfFont = family;
}
